// Core
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import {
  Chip,
  DataTable,
  KpiStrip,
  LineChart,
  RemoteTable,
  SectionCard,
  Segmented,
  Tabs,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Pages
import BalanceTable from "pages/ReadSettlement/Settlement/BalanceTable";

// Redux
import { useSelector } from "react-redux";
import { selectTimeSelector } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectAllMembers, useGetMembersByCommunityIdQuery } from "state/members/membersApiSlice";
import {
  selectAllResources,
  useGetResourcesByCommunityIdQuery,
} from "state/resources/resourcesApiSlice";
import { useGetCommunitySavingsByIdQuery } from "state/communities/communitiesApiSlice";
import {
  useGetSessionsByCommunityIdPaginationQuery,
  useGetSettlementBySessionIdQuery,
} from "state/sessions/sessionApiSlice";
import {
  useGetSettlementMembersKPIsByCommunityIdQuery,
  useGetSettlementMembersMetricsByCommunityIdQuery,
} from "state/sessions/settlementApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";
import { createChartXAxisLabel } from "utils/charts";
import { datetimeToDate, datetimeToTime } from "utils/time";

const fmt = (v, d = 2) =>
  v != null ? Number(v).toLocaleString(undefined, { maximumFractionDigits: d }) : "0";

// The savings chart aggregates to daily resolution; the sessions table keeps
// the raw DB granularity via its own paginated query.
const SAVINGS_GRANULARITY = "DAY";

function SettlementCharts() {
  const { t } = useTranslation(["settlement"]);
  const community = useSelector(selectCurrentCommunity);
  const { granularity, startDate, endDate } = useSelector(selectTimeSelector);

  const queryArgs = { communityId: community.id, granularity, startDate, endDate };

  const { data: communitySavings } = useGetCommunitySavingsByIdQuery({
    ...queryArgs,
    granularity: SAVINGS_GRANULARITY,
  });
  const { data: settlementMemberKPIs } = useGetSettlementMembersKPIsByCommunityIdQuery(queryArgs);
  const { data: settlementMemberMetrics } =
    useGetSettlementMembersMetricsByCommunityIdQuery(queryArgs);

  // GET members
  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: membersData }) => ({
        members: membersData && selectAllMembers(membersData),
      }),
    }
  );

  const getMemberName = (memberId) => {
    const member = members?.find((m) => m.id === memberId);
    return member?.name || memberId;
  };

  // Convert dictionary to array format
  const metrics = useMemo(
    () =>
      settlementMemberMetrics
        ? Object.entries(settlementMemberMetrics).map(([memberId, m]) => ({
            member_id: memberId,
            ...m,
          }))
        : [],
    [settlementMemberMetrics]
  );

  const kpis = settlementMemberKPIs || {};

  // Savings chart
  const savingsLabels = (communitySavings || []).map((m) =>
    createChartXAxisLabel(m.datetime_timestamp, SAVINGS_GRANULARITY)
  );
  const savingsSeries = [
    {
      name: t("analytics.charts.community_savings.series_label"),
      data: (communitySavings || []).map((m) => m.savings),
      color: "var(--accent)",
    },
  ];

  const num = (v, color) => (
    <span className="tabular-nums" style={color ? { color, fontWeight: 600 } : undefined}>
      {fmt(v, 2)}
    </span>
  );

  const memberCell = (v) => (
    <div className="rec-cell-member">
      <b>{v}</b>
    </div>
  );

  const buySellRows = useMemo(
    () =>
      metrics.map((m, i) => ({
        id: i,
        member: getMemberName(m.member_id),
        e_sup: m.e_sup,
        e_pur: m.e_pur,
        e_sale: m.e_sale,
        e_sur: m.e_sur,
      })),
    [metrics, members]
  );

  const buySellCols = [
    {
      key: "member",
      label: t("analytics.tables.buy_sell.columns.member"),
      render: memberCell,
    },
    {
      key: "e_sup",
      label: t("analytics.tables.buy_sell.columns.e_sup"),
      align: "right",
      render: (v) => num(v),
    },
    {
      key: "e_pur",
      label: t("analytics.tables.buy_sell.columns.energy_bought_acc"),
      align: "right",
      render: (v) => num(v, "var(--info)"),
    },
    {
      key: "e_sale",
      label: t("analytics.tables.buy_sell.columns.energy_sold_acc"),
      align: "right",
      render: (v) => num(v, "var(--accent)"),
    },
    {
      key: "e_sur",
      label: t("analytics.tables.buy_sell.columns.e_sur"),
      align: "right",
      render: (v) => num(v),
    },
  ];

  const balanceRows = useMemo(
    () =>
      metrics.map((m, i) => ({
        id: i,
        member: getMemberName(m.member_id),
        rev_sup: m.rev_sup,
        rev_pur: m.rev_pur,
        rev_sale: m.rev_sale,
        rev_sur: m.rev_sur,
        rev_slc_soc: m.rev_slc_soc,
        rev_lem_fee: m.rev_lem_fee,
        rev_pow: m.rev_pow,
        rev_total_pow: m.rev_total_pow,
        savings: m.savings,
      })),
    [metrics, members]
  );

  return (
    <div className="flex flex-col gap-rec">
      <KpiStrip
        cols={2}
        items={[
          {
            icon: "savings",
            value: fmt(kpis.savings, 2),
            unit: "€",
            sub: `(${fmt(kpis.savings_perc, 2)} %)`,
            label: t("analytics.kpis.community_savings.title"),
            tint: "green",
          },
          {
            icon: "account_balance",
            value: fmt(kpis.rev_total_pow, 2),
            unit: "€",
            label: t("analytics.kpis.community_total_balance.title"),
            tint: "teal",
          },
        ]}
      />

      <SectionCard
        icon="show_chart"
        title={t("analytics.charts.community_savings.title")}
        sub={t("analytics.charts.community_savings.series_label")}
      >
        <LineChart
          series={savingsSeries}
          labels={savingsLabels}
          unit="€"
          height={300}
          type="area"
          xTitle={t("charts.axis_date", { ns: "common" })}
          yTitle="€"
        />
      </SectionCard>

      <div className="rec-card">
        <div className="rec-card-pad pb-0">
          <h3 className="rec-section-title">
            <Icon name="swap_horiz" />
            {t("analytics.tables.buy_sell.title")}
          </h3>
        </div>
        <DataTable
          columns={buySellCols}
          rows={buySellRows}
          search={t("analytics.tables.search_member", { defaultValue: "Procurar membro…" })}
          searchKeys={["member"]}
          footer={false}
        />
      </div>

      <div className="rec-card">
        <div className="rec-card-pad pb-0">
          <h3 className="rec-section-title">
            <Icon name="account_balance" />
            {t("analytics.tables.economic_balance.title")}
          </h3>
        </div>
        <BalanceTable rows={balanceRows} />
      </div>
    </div>
  );
}

const fmtMoney = (v) => `${fmt(v, 2)} €`;

const moneyCell = (v, { strong } = {}) => (
  <span
    className="tabular-nums"
    style={strong ? { fontWeight: 700, color: "var(--ink)" } : undefined}
  >
    {fmtMoney(v)}
  </span>
);

/* Detail panel for an expanded session: per-session member / member-resource
   settlement, fetched from the per-session settlement endpoint. */
function SettlementSessionDetail({ communityId, session, members, resources, onClose }) {
  const { t } = useTranslation(["settlement", "common"]);
  const [detailTab, setDetailTab] = useState("members");

  useEffect(() => {
    setDetailTab("members");
  }, [session.id]);

  const { data: settlement, isFetching } = useGetSettlementBySessionIdQuery({
    communityId,
    sessionId: session.id,
    excludeDeleted: "false",
  });

  const memberName = (id) => members?.find((m) => m.id === id)?.name || id;
  const resourceName = (id) => resources?.find((r) => r.id === id)?.name || id;

  const memberRows = useMemo(
    () =>
      settlement?.members
        ? Object.entries(settlement.members).map(([memberId, d]) => ({
            id: memberId,
            member: memberName(memberId),
            rev_total_pow: d.rev_total_pow,
            rev_total_pow_no_csc: d.rev_total_pow_no_csc,
            rev_lem_trd: d.rev_lem_trd,
            rev_pow: d.rev_pow,
            rev_slc_soc: d.rev_slc_soc,
            rev_sup: d.rev_sup,
            rev_sur: d.rev_sur,
          }))
        : [],
    [settlement, members]
  );

  const memberResourceRows = useMemo(
    () =>
      settlement?.member_resources
        ? Object.entries(settlement.member_resources).flatMap(([memberId, resourcesMap]) =>
            Object.entries(resourcesMap).map(([resourceId, d]) => ({
              id: `${memberId}:${resourceId}`,
              member: memberName(memberId),
              resource: resourceName(resourceId),
              rev_total_pow: d.rev_total_pow,
              rev_lem_trd: d.rev_lem_trd,
              rev_pow: d.rev_pow,
              rev_sup: d.rev_sup,
              rev_sur: d.rev_sur,
            }))
          )
        : [],
    [settlement, members, resources]
  );

  const mCols = t("tables.members.columns", { returnObjects: true });
  const mrCols = t("tables.member_resource.columns", { returnObjects: true });

  const memberCell = (v) => (
    <div className="rec-cell-member">
      <div className="rec-av-sm" style={{ background: avColor(v) }}>
        {initials(v)}
      </div>
      <b>{v}</b>
    </div>
  );

  const memberColumns = [
    { key: "member", label: mCols.member, width: "17%", render: memberCell },
    {
      key: "rev_total_pow",
      label: mCols.balance,
      align: "right",
      render: (v) => moneyCell(v, { strong: true }),
    },
    {
      key: "rev_total_pow_no_csc",
      label: mCols.rev_total_no_csc,
      align: "right",
      render: moneyCell,
    },
    { key: "rev_lem_trd", label: mCols.rev_lem_trd, align: "right", render: moneyCell },
    { key: "rev_pow", label: mCols.rev_pow, align: "right", render: moneyCell },
    { key: "rev_slc_soc", label: mCols.rev_slc_soc, align: "right", render: moneyCell },
    { key: "rev_sup", label: mCols.rev_sup, align: "right", render: moneyCell },
    { key: "rev_sur", label: mCols.rev_sur, align: "right", render: moneyCell },
  ];

  const memberResourceColumns = [
    {
      key: "resource",
      label: mrCols.resource,
      width: "18%",
      render: (v) => <b style={{ color: "var(--ink)" }}>{v}</b>,
    },
    {
      key: "member",
      label: mrCols.member,
      width: "16%",
      render: (v) => <span style={{ color: "var(--ink-2)" }}>{v}</span>,
    },
    {
      key: "rev_total_pow",
      label: mrCols.rev_total,
      align: "right",
      render: (v) => moneyCell(v, { strong: true }),
    },
    { key: "rev_lem_trd", label: mrCols.rev_lem_trd, align: "right", render: moneyCell },
    { key: "rev_pow", label: mrCols.rev_pow, align: "right", render: moneyCell },
    { key: "rev_sup", label: mrCols.rev_sup, align: "right", render: moneyCell },
    { key: "rev_sur", label: mrCols.rev_sur, align: "right", render: moneyCell },
  ];

  return (
    <div className="rec-card overflow-hidden p-0">
      <div className="flex items-center gap-[14px] flex-wrap px-[var(--pad-card,20px)] py-[14px] border-b border-[var(--border)] bg-[var(--surface-2)]">
        <div className="flex items-center gap-[9px] flex-1 min-w-[200px] text-[14px] font-bold text-ink">
          <Icon name="receipt_long" style={{ color: "var(--accent)" }} />
          {t("single.header", { date: session.date, time: session.time })}
        </div>
        <Segmented
          value={detailTab}
          onChange={setDetailTab}
          options={[
            { value: "members", label: `${t("single.tabs.members")} (${memberRows.length})` },
            {
              value: "member_resources",
              label: `${t("single.tabs.members_resources")} (${memberResourceRows.length})`,
            },
          ]}
        />
        <button
          type="button"
          className="rec-btn-ghost rec-btn-sm"
          onClick={onClose}
          title={t("table.clear", { ns: "common", defaultValue: "Fechar" })}
        >
          <Icon name="close" />
        </button>
      </div>
      <div className="rec-tbl-fit">
        {detailTab === "members" ? (
          <DataTable
            key="members"
            columns={memberColumns}
            rows={memberRows}
            maxRows={8}
            footer={false}
            search={false}
            emptyText={
              isFetching
                ? t("table.loading", { ns: "common", defaultValue: "A carregar…" })
                : undefined
            }
          />
        ) : (
          <DataTable
            key="member_resources"
            columns={memberResourceColumns}
            rows={memberResourceRows}
            maxRows={8}
            footer={false}
            search={false}
            emptyText={
              isFetching
                ? t("table.loading", { ns: "common", defaultValue: "A carregar…" })
                : undefined
            }
          />
        )}
      </div>
    </div>
  );
}

SettlementSessionDetail.propTypes = {
  communityId: PropTypes.string.isRequired,
  session: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    date: PropTypes.string,
    time: PropTypes.string,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  members: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  resources: PropTypes.array,
  onClose: PropTypes.func.isRequired,
};

SettlementSessionDetail.defaultProps = {
  members: [],
  resources: [],
};

/* Settled sessions for the period (server-paginated). Expanding a row reveals a
   detail panel with the per-session member / member-resource settlement. */
function SettlementSessions() {
  const { t } = useTranslation(["settlement", "common"]);
  const community = useSelector(selectCurrentCommunity);

  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [openId, setOpenId] = useState(null);

  const { data, isFetching } = useGetSessionsByCommunityIdPaginationQuery(
    {
      communityId: community.id,
      page,
      limit,
      status: "SETTLED",
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    { pollingInterval: 15000, refetchOnFocus: true, refetchOnMountOrArgChange: true }
  );

  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: membersData }) => ({
        members: membersData && selectAllMembers(membersData),
      }),
    }
  );

  const { resources } = useGetResourcesByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: resourcesData }) => ({
        resources: resourcesData && selectAllResources(resourcesData),
      }),
    }
  );

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((s, i) => ({
        id: s.id ?? i,
        date: datetimeToDate(s.datetime_timestamp),
        time: datetimeToTime(s.datetime_timestamp),
        transaction_count: Number(s.transaction_count) || 0,
        offer_count: Number(s.offer_count) || 0,
      })),
    [data]
  );

  const openSession = useMemo(() => rows.find((r) => r.id === openId) || null, [rows, openId]);

  const columns = [
    {
      key: "date",
      label: t("table.columns.date", { defaultValue: "Data" }),
      sortable: true,
      render: (v) => <b style={{ color: "var(--ink)" }}>{v}</b>,
    },
    {
      key: "time",
      label: t("table.columns.time", { defaultValue: "Hora" }),
      sortable: true,
      render: (v) => <span className="tabular-nums text-ink-2">{v}</span>,
    },
    {
      key: "transaction_count",
      label: t("table.columns.transactions", { defaultValue: "Transações" }),
      align: "right",
      render: (v) => (
        <Chip tone="gray">
          {v} {t("table.unit_tx", { defaultValue: "TX" })}
        </Chip>
      ),
    },
    {
      key: "offer_count",
      label: t("table.columns.offers", { defaultValue: "Ofertas" }),
      align: "right",
      render: (v) => (
        <Chip tone="gray">
          {v} {t("table.unit_of", { defaultValue: "OF" })}
        </Chip>
      ),
    },
    {
      key: "id",
      label: "",
      align: "right",
      render: (v) => (
        <button
          type="button"
          className="rec-btn-ghost rec-btn-sm"
          onClick={() => setOpenId((prev) => (prev === v ? null : v))}
          aria-expanded={openId === v}
        >
          <Icon
            name={openId === v ? "expand_less" : "chevron_right"}
            style={{ fontSize: 20, color: openId === v ? "var(--accent)" : "var(--ink-3)" }}
          />
        </button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-rec">
      <div className="rec-card">
        <RemoteTable
          columns={columns}
          rows={rows}
          totalCount={data?.total_count ?? 0}
          page={page}
          limit={limit}
          onPageChange={(p) => {
            setOpenId(null);
            setPage(p);
          }}
          sort={sortOrder}
          onSortChange={(s) => {
            setOpenId(null);
            setPage(0);
            setSortOrder(s);
          }}
          loading={isFetching}
        />
      </div>

      {openSession && (
        <SettlementSessionDetail
          communityId={community.id}
          session={openSession}
          members={members}
          resources={resources}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

function Settlement() {
  const { t } = useTranslation(["settlement"]);
  const [tab, setTab] = useState("charts");

  return (
    <div>
      <Tabs
        tabs={[
          { id: "charts", label: t("page.tabs.charts"), icon: "insights" },
          { id: "table", label: t("page.tabs.table"), icon: "table_rows" },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === "charts" && <SettlementCharts />}
      {tab === "table" && <SettlementSessions />}
    </div>
  );
}

export default Settlement;
